import React, { useCallback, useMemo, useState } from "react";
import Spinner from "src/components/common/Spinner";
import SearchBar from "src/components/common/SearchBar";
import DeleteItemModal from "src/components/common/DeleteItemModal";
import CustomersTable, {
  type CustomerSortKey,
  type CustomerSortOrder,
} from "src/features/Customers/components/CustomersTable";
import { ManageCustomerModal, type ManageCustomerMode } from "src/features/Customers/components/ManageCustomerModal";
import { useStoreCustomers } from "src/features/Customers/hooks/useStoreCustomers";
import { StoreCustomerEdge, StoreCustomer } from "src/interfaces/customer.interface";
import { useAuth } from "src/contexts/auth-provider";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { DELETE_STORE_CUSTOMER } from "src/constants/mutations";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { failureToast, successToast } from "src/utils/toasts";

const PAGE_SIZE = 10;

const MAX_VISIBLE_PAGES = 7;

function sortEdges(
  edges: StoreCustomerEdge[],
  sortKey: CustomerSortKey,
  sortOrder: CustomerSortOrder
): StoreCustomerEdge[] {
  if (!sortKey) return [...edges];
  return [...edges].sort((a, b) => {
    const nodeA = a.node;
    const nodeB = b.node;
    let cmp = 0;
    if (sortKey === "name") {
      const na = (nodeA.name ?? "").trim().toLowerCase();
      const nb = (nodeB.name ?? "").trim().toLowerCase();
      cmp = na.localeCompare(nb);
    } else if (sortKey === "visits") {
      const va = nodeA.visitTimes ?? 0;
      const vb = nodeB.visitTimes ?? 0;
      cmp = va - vb;
    } else if (sortKey === "lastVisit") {
      const ta = nodeA.lastVisitTime ?? 0;
      const tb = nodeB.lastVisitTime ?? 0;
      cmp = ta - tb;
    }
    return sortOrder === "asc" ? cmp : -cmp;
  });
}

function getPageNumbers(currentPage: number, totalPages: number): (number | "ellipsis")[] {
  if (totalPages <= MAX_VISIBLE_PAGES) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages: (number | "ellipsis")[] = [];
  const showLeft = currentPage > 2;
  const showRight = currentPage < totalPages - 1;

  pages.push(1);
  if (showLeft && currentPage > 3) pages.push("ellipsis");

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);
  for (let i = start; i <= end; i++) {
    if (i !== 1 && i !== totalPages) pages.push(i);
  }

  if (showRight && currentPage < totalPages - 2) pages.push("ellipsis");
  if (totalPages > 1) pages.push(totalPages);

  return pages;
}

const BORDER_LIGHT = "rgba(0,0,0,0.06)";

interface CustomersPaginationProps {
  currentPage: number;
  totalPages: number;
  total: number;
  start: number;
  end: number;
  pageNumbers: (number | "ellipsis")[];
  onPrevPage: () => void;
  onNextPage: () => void;
  onGoToPage: (page: number) => void;
  canGoToPage: (page: number) => boolean;
}

const CustomersPagination = React.memo(function CustomersPagination({
  currentPage,
  totalPages,
  total,
  start,
  end,
  pageNumbers,
  onPrevPage,
  onNextPage,
  onGoToPage,
  canGoToPage,
}: CustomersPaginationProps) {
  return (
    <section
      className="shrink-0 py-3.5 px-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-t bg-[#fafafa] relative"
      style={{ borderColor: BORDER_LIGHT }}
      aria-label="Pagination"
    >
      <p className="text-sm text-gray-500 order-2 sm:order-1 sm:flex-1 sm:min-w-0 sm:text-left">
        Showing {start}–{end} of {total}
      </p>
      <div className="flex items-center justify-center gap-2 flex-wrap order-1 sm:order-2 sm:flex-none sm:absolute sm:left-1/2 sm:-translate-x-1/2">
        <button
          type="button"
          className="h-9 min-w-[56px] w-14 flex items-center justify-center text-sm font-medium text-[#252525] bg-white border border-[#F1F1F2] rounded-lg disabled:opacity-50 disabled:cursor-default focus:outline-none hover:bg-[#fafafa]"
          onClick={onPrevPage}
          disabled={currentPage <= 1}
        >
          Prev
        </button>
        <div className="flex items-center gap-1">
          {pageNumbers.map((item, idx) =>
            item === "ellipsis" ? (
              <span
                key={`ellipsis-${idx}`}
                className="px-2 text-gray-400 text-sm"
              >
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                className={`min-w-[36px] w-9 h-9 rounded-lg text-sm font-medium flex items-center justify-center focus:outline-none ${
                  currentPage === item
                    ? "bg-orange text-white"
                    : "bg-gray-50 text-gray-700"
                } disabled:opacity-50 disabled:cursor-default`}
                onClick={() => onGoToPage(item)}
                disabled={!canGoToPage(item)}
              >
                {item}
              </button>
            )
          )}
        </div>
        <button
          type="button"
          className="h-9 min-w-[56px] w-14 flex items-center justify-center text-sm font-medium text-[#252525] bg-white border border-[#F1F1F2] rounded-lg disabled:opacity-50 disabled:cursor-default focus:outline-none hover:bg-[#fafafa]"
          onClick={onNextPage}
          disabled={currentPage >= totalPages}
        >
          Next
        </button>
      </div>
      <div className="hidden sm:block flex-1 min-w-0 order-3" aria-hidden="true" />
    </section>
  );
});

const CustomersPage = () => {
  const { hasRestaurant } = useAuth();
  const { axBe } = useAxiosInterceptor();
  const {
    edges,
    pageInfo,
    isLoading,
    currentPage,
    totalPages,
    goToPage,
    loadNextPage,
    loadPrevPage,
    applySearch,
    canGoToPage,
    refetch,
  } = useStoreCustomers(PAGE_SIZE);

  const [sortKey, setSortKey] = useState<CustomerSortKey>(null);
  const [sortOrder, setSortOrder] = useState<CustomerSortOrder>("asc");
  const [customerModal, setCustomerModal] = useState<{
    mode: ManageCustomerMode;
    customer: StoreCustomer | null;
  } | null>(null);
  const [customerToDelete, setCustomerToDelete] = useState<StoreCustomer | null>(null);
  const [isDeletingCustomer, setIsDeletingCustomer] = useState(false);

  const handleAddCustomer = useCallback(() => {
    setCustomerModal({ mode: "create", customer: null });
  }, []);

  const handleViewCustomer = useCallback((customer: StoreCustomer) => {
    setCustomerModal({ mode: "view", customer });
  }, []);

  const handleEditCustomer = useCallback((customer: StoreCustomer) => {
    setCustomerModal({ mode: "edit", customer });
  }, []);

  const handleDeleteCustomer = useCallback((customer: StoreCustomer) => {
    setCustomerToDelete(customer);
  }, []);

  const handleCloseCustomerModal = useCallback(() => {
    setCustomerModal(null);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setCustomerToDelete(null);
  }, []);

  const handleConfirmDeleteCustomer = useCallback(async () => {
    if (!customerToDelete?.correlationId || !hasRestaurant?.correlationId) return;
    setIsDeletingCustomer(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: DELETE_STORE_CUSTOMER,
        variables: {
          storeCorrelationId: hasRestaurant.correlationId,
          storeCustomerCorrelationId: customerToDelete.correlationId,
        },
      });
      if (data?.errors?.length) throw data.errors;
      successToast("Customer removed");
      refetch();
      setCustomerToDelete(null);
    } catch (err) {
      console.error("Error deleting customer:", err);
      failureToast("Failed to delete customer");
    } finally {
      setIsDeletingCustomer(false);
    }
  }, [customerToDelete, hasRestaurant?.correlationId, axBe, refetch]);

  const sortedEdges = useMemo(
    () => sortEdges(edges ?? [], sortKey, sortOrder),
    [edges, sortKey, sortOrder]
  );

  const handleSort = (key: CustomerSortKey) => {
    if (!key) return;
    setSortKey(key);
    setSortOrder((prev) => (sortKey === key && prev === "asc" ? "desc" : "asc"));
  };

  const total = pageInfo?.totalNumberOfCustomers ?? 0;
  const start = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const end = Math.min(currentPage * PAGE_SIZE, total);

  const pageNumbers = useMemo(
    () => getPageNumbers(currentPage, totalPages),
    [currentPage, totalPages]
  );

  return (
    <div className="w-full max-w-full pt-0 pb-0 flex-1 min-h-0 flex flex-col">
      <div
        className="bg-white rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.04)] overflow-hidden flex-1 flex flex-col min-h-0"
        style={{ border: `1px solid ${BORDER_LIGHT}` }}
      >
        {/* Part 1: Search + Add Customer only */}
        <section
          className="shrink-0 py-2 px-3 border-b flex flex-row items-center justify-between gap-3 min-h-[42px]"
          style={{ borderColor: BORDER_LIGHT }}
          aria-label="Customers toolbar"
        >
          <div className="flex-1 min-w-0 max-w-[260px]">
            <SearchBar
              placeholder="Search by name"
              onDebouncedChange={applySearch}
              debounceMs={300}
            />
          </div>
          <button
            type="button"
            className="h-8 px-4 rounded-lg text-sm font-medium text-white bg-[#FD7657] hover:bg-[#e45e41] shrink-0 transition-colors"
            onClick={handleAddCustomer}
          >
            Add Customer
          </button>
        </section>

        {isLoading && !pageInfo ? (
          <div className="flex justify-center py-12 flex-1 min-h-[120px]">
            <Spinner />
          </div>
        ) : (edges ?? []).length === 0 ? (
          <p className="text-gray-500 text-sm py-12 text-center flex-1">
            No customers found
          </p>
        ) : (
          <>
            {/* Part 2: Table only – fixed header, scrollable body */}
            <section
              className="flex-1 min-h-0 flex flex-col min-w-0 relative"
              aria-label="Customers table"
              aria-busy={isLoading}
            >
              {isLoading && (
                <div
                  className="absolute inset-0 z-20 flex items-center justify-center bg-white/70"
                  aria-hidden="true"
                >
                  <Spinner />
                </div>
              )}
              <div className="flex-1 min-h-0 min-w-0 flex flex-col">
              <CustomersTable
                edges={sortedEdges}
                sortKey={sortKey}
                sortOrder={sortOrder}
                onSort={handleSort}
                onViewCustomer={handleViewCustomer}
                onEditCustomer={handleEditCustomer}
                onDeleteCustomer={handleDeleteCustomer}
              />
              </div>
            </section>

            {/* Part 3: Page navigation */}
            <CustomersPagination
              currentPage={currentPage}
              totalPages={totalPages}
              total={total}
              start={start}
              end={end}
              pageNumbers={pageNumbers}
              onPrevPage={loadPrevPage}
              onNextPage={loadNextPage}
              onGoToPage={goToPage}
              canGoToPage={canGoToPage}
            />
          </>
        )}
      </div>

      {customerModal && (
        <ManageCustomerModal
          mode={customerModal.mode}
          customer={customerModal.customer}
          onClose={handleCloseCustomerModal}
          onSuccess={refetch}
        />
      )}

      <DeleteItemModal
        isOpen={!!customerToDelete}
        onClose={handleCloseDeleteModal}
        itemType="customer"
        onDelete={handleConfirmDeleteCustomer}
        isDeletingItem={isDeletingCustomer}
      />
    </div>
  );
};

export default CustomersPage;
